import * as React from 'react';

import type { KbAdminClient, KbClient } from '@/api/kb-client';
import { HttpKbClient } from '@/api/kb-client.http';

// =============================================================================
// kb-client-context.tsx — Injection du KbClient (DI seam, R15-COM-04).
// =============================================================================
// DEUX seams distincts : le contexte ADMIN porte un KbAdminClient (LECTURE + tags + export, base
// `/api/communication/admin/kb`) ; le contexte FOURNISSEUR porte un KbClient (LECTURE SEULE, base
// `/api/communication/kb`). Câblé HTTP DÈS LE DÉPART (garde 09_stub_in_prod_guard : aucun défaut
// stub-en-prod). Les tests de composant injectent un StubKbClient via le provider adéquat.
//
// `useKbClient(profile)` sert la LECTURE des deux surfaces ; `useKbAdminClient()` expose la surface
// ADMIN étendue (tags/export) — jamais résolue côté fournisseur (§3.9).

/** Adaptateur ADMIN par défaut : HTTP réel (base `/api/communication/admin/kb`, CSRF `admin_csrf`). */
const defaultAdminKbClient: KbAdminClient = new HttpKbClient('admin');
/** Adaptateur FOURNISSEUR par défaut : HTTP réel (base `/api/communication/kb`, LECTURE SEULE). */
const defaultSupplierKbClient: KbClient = new HttpKbClient('supplier');

const KbAdminClientContext =
  React.createContext<KbAdminClient>(defaultAdminKbClient);
const KbSupplierClientContext = React.createContext<KbClient>(
  defaultSupplierKbClient,
);

export interface KbAdminClientProviderProps {
  client?: KbAdminClient | undefined;
  children: React.ReactNode;
}

export interface KbSupplierClientProviderProps {
  client?: KbClient | undefined;
  children: React.ReactNode;
}

export function KbAdminClientProvider({
  client = defaultAdminKbClient,
  children,
}: KbAdminClientProviderProps): React.JSX.Element {
  return (
    <KbAdminClientContext.Provider value={client}>
      {children}
    </KbAdminClientContext.Provider>
  );
}

export function KbSupplierClientProvider({
  client = defaultSupplierKbClient,
  children,
}: KbSupplierClientProviderProps): React.JSX.Element {
  return (
    <KbSupplierClientContext.Provider value={client}>
      {children}
    </KbSupplierClientContext.Provider>
  );
}

/** Lit le KbClient (LECTURE) du profil demandé (admin / supplier) depuis le contexte correspondant. */
export function useKbClient(profile: 'admin' | 'supplier'): KbClient {
  const admin = React.useContext(KbAdminClientContext);
  const supplier = React.useContext(KbSupplierClientContext);
  return profile === 'admin' ? admin : supplier;
}

/** Lit la surface ADMIN étendue (tags/export) — à n'utiliser que dans les écrans admin. */
export function useKbAdminClient(): KbAdminClient {
  return React.useContext(KbAdminClientContext);
}
